import React from "react";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";

const Hero = () => {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen pt-28 md:pt-36 pb-12 bg-sunrise-soft overflow-hidden grain"
        >
            {/* Decorative sun rays */}
            <div className="absolute -top-32 -right-32 w-[42rem] h-[42rem] sun-rays opacity-30 animate-spin-slow pointer-events-none" />
            <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-sunrise opacity-30 blur-3xl pointer-events-none animate-sun-pulse" />

            <div className="container-x relative">
                {/* Top labels */}
                <div className="flex items-center justify-between mb-12 md:mb-16 border-b border-sun-line pb-4">
                    <div className="overline">— A wellness brand from Surat, India</div>
                    <div className="overline hidden md:block">Make your health better</div>
                    <div className="overline">Est · 2025</div>
                </div>

                {/* Big headline */}
                <div className="grid grid-cols-12 gap-6 md:gap-8 items-end mb-12 md:mb-20">
                    <div className="col-span-12 md:col-span-9">
                        <div className="inline-flex items-center gap-2 mb-8 pill bg-white border border-sun-line px-4 py-2">
                            <Sparkles className="w-3.5 h-3.5 text-sun-orange" strokeWidth={2} />
                            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-sun-ink">
                                100% chemical-free · Naturally dried
                            </span>
                        </div>
                        <h1 className="font-heading leading-[0.95] tracking-[-0.02em] text-sun-ink">
                            <span className="block text-[16vw] md:text-[12vw] lg:text-[10rem] font-normal">
                                Make
                            </span>
                            <span className="block text-[16vw] md:text-[12vw] lg:text-[10rem] font-normal pl-[8vw] md:pl-[14vw]">
                                <span className="display-italic text-gradient">your health</span>
                            </span>
                            <span className="block text-[16vw] md:text-[12vw] lg:text-[10rem] font-normal">
                                better.
                            </span>
                        </h1>
                    </div>
                    <div className="col-span-12 md:col-span-3 md:pb-6">
                        <p className="font-body text-base md:text-lg text-sun-inkSoft leading-relaxed border-l-2 border-sun-orange pl-5 max-w-xs">
                            Single-origin fruit & vegetable powders — naturally dried, never compromised. Crafted in Surat, sent to fifty pantries worldwide.
                        </p>
                    </div>
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap gap-3 mb-12 md:mb-16">
                    <a href="#products" data-testid="hero-shop-cta" className="btn-gradient">
                        Shop the catalogue
                        <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                    </a>
                    <a href="#bulk" data-testid="hero-bulk-cta" className="btn-outline">
                        Trade enquiry
                    </a>
                </div>

                {/* Featured collage strip */}
                <div className="grid grid-cols-12 gap-3 md:gap-4">
                    <div className="col-span-12 md:col-span-6 aspect-[5/4] md:aspect-[5/4] overflow-hidden bg-white relative group rounded-3xl border border-sun-line">
                        <img
                            src="https://images.pexels.com/photos/4198755/pexels-photo-4198755.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200"
                            alt="Spoons of turmeric and paprika"
                            className="w-full h-full object-cover transition-transform duration-[1800ms] group-hover:scale-110"
                            loading="eager"
                        />
                        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                            <div className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-2xl">
                                <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-sun-orange">
                                    Pure pigments
                                </div>
                                <div className="font-heading text-2xl text-sun-ink mt-1 leading-tight">
                                    From farm to fine powder
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-span-6 md:col-span-3 flex flex-col gap-3 md:gap-4">
                        <div className="bg-sunrise text-white p-6 md:p-8 flex flex-col justify-between flex-1 relative overflow-hidden rounded-3xl">
                            <div className="font-mono text-[10px] uppercase tracking-[0.32em] opacity-80">
                                / The numbers
                            </div>
                            <div>
                                <div className="font-heading text-[5rem] md:text-[6rem] leading-[0.85]">
                                    18
                                </div>
                                <div className="mt-2 font-body text-xs uppercase tracking-[0.2em] opacity-90">
                                    single-origin powders
                                </div>
                            </div>
                            <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/10" />
                        </div>
                        <div className="bg-sun-ink text-sun-bg p-6 md:p-8 flex flex-col justify-between flex-1 rounded-3xl">
                            <div className="font-mono text-[10px] uppercase tracking-[0.32em] opacity-70">
                                / Reach
                            </div>
                            <div>
                                <div className="font-heading text-[3rem] md:text-[3.5rem] leading-[0.9]">
                                    2+
                                </div>
                                <div className="mt-1 font-body text-xs opacity-80">
                                    countries served
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-span-6 md:col-span-3 aspect-square md:aspect-auto bg-white overflow-hidden relative group rounded-3xl border border-sun-line">
                        <img
                            src="https://images.pexels.com/photos/11703280/pexels-photo-11703280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=900"
                            alt="Spice sacks"
                            className="w-full h-full object-cover transition-transform duration-[1800ms] group-hover:scale-110"
                            loading="eager"
                        />
                    </div>
                </div>

                <a
                    href="#products"
                    className="mt-12 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-sun-ink hover:text-sun-orange transition-colors"
                >
                    Scroll to the catalogue
                    <ArrowDown className="w-4 h-4" strokeWidth={2} />
                </a>
            </div>
        </section>
    );
};

export default Hero;
